/**
 * Servicios para gestión financiera
 * Cobros, Pagos y Gastos
 */

import { where, orderBy, Timestamp } from "firebase/firestore";
import { getById, getAll, create, update, subscribeToCollection } from "./base";
import type { Charge, Payment, Expense, ChargeStatus } from "@/types";

// ==================== CHARGES (COBROS) ====================

export const chargeService = {
  async getById(id: string): Promise<Charge | null> {
    return getById<Charge>("charges", id);
  },

  async getByClub(clubId: string): Promise<Charge[]> {
    return getAll<Charge>("charges", [
      where("clubId", "==", clubId),
      orderBy("createdAt", "desc"),
    ]);
  },

  async getByClubAndPeriod(
    clubId: string,
    periodId: string,
  ): Promise<Charge[]> {
    return getAll<Charge>("charges", [
      where("clubId", "==", clubId),
      where("periodId", "==", periodId),
      orderBy("createdAt", "desc"),
    ]);
  },

  async getByPerson(personId: string): Promise<Charge[]> {
    return getAll<Charge>("charges", [
      where("personId", "==", personId),
      orderBy("createdAt", "desc"),
    ]);
  },

  async getByMember(memberId: string): Promise<Charge[]> {
    return getAll<Charge>("charges", [
      where("memberId", "==", memberId),
      orderBy("createdAt", "desc"),
    ]);
  },

  async getPendingByClub(clubId: string): Promise<Charge[]> {
    return getAll<Charge>("charges", [
      where("clubId", "==", clubId),
      where("status", "in", ["pending", "partial", "overdue"]),
      orderBy("createdAt", "desc"),
    ]);
  },

  async create(
    data: Omit<
      Charge,
      "id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
    >,
    userId: string,
  ): Promise<string> {
    return create<Charge>("charges", data, userId);
  },

  async update(id: string, data: Partial<Charge>): Promise<void> {
    return update<Charge>("charges", id, data);
  },

  async updateStatus(id: string, status: ChargeStatus): Promise<void> {
    return update<Charge>("charges", id, { status });
  },

  // Actualizar balance después de un pago
  async updateBalance(
    id: string,
    amountPaid: number,
    balance: number,
  ): Promise<void> {
    // "partial" en cuanto haya ALGÚN abono y aún quede saldo. (Antes usaba
    // `balance < amountPaid`, que solo marcaba parcial si se pagó >50% → un
    // abono pequeño quedaba mal clasificado como "pending".)
    const status: ChargeStatus =
      balance <= 0 ? "paid" : amountPaid > 0 ? "partial" : "pending";
    return update<Charge>("charges", id, { amountPaid, balance, status });
  },
};

// ==================== PAYMENTS (PAGOS) ====================

export const paymentService = {
  async getById(id: string): Promise<Payment | null> {
    return getById<Payment>("payments", id);
  },

  async getByClub(clubId: string): Promise<Payment[]> {
    return getAll<Payment>("payments", [
      where("clubId", "==", clubId),
      orderBy("paymentDate", "desc"),
    ]);
  },

  async getByClubAndPeriod(
    clubId: string,
    periodId: string,
  ): Promise<Payment[]> {
    return getAll<Payment>("payments", [
      where("clubId", "==", clubId),
      where("periodId", "==", periodId),
      orderBy("paymentDate", "desc"),
    ]);
  },

  async getByCharge(chargeId: string): Promise<Payment[]> {
    return getAll<Payment>("payments", [
      where("chargeId", "==", chargeId),
      orderBy("paymentDate", "desc"),
    ]);
  },

  async getByPerson(personId: string): Promise<Payment[]> {
    return getAll<Payment>("payments", [
      where("personId", "==", personId),
      orderBy("paymentDate", "desc"),
    ]);
  },

  async getByMember(memberId: string): Promise<Payment[]> {
    return getAll<Payment>("payments", [
      where("memberId", "==", memberId),
      orderBy("paymentDate", "desc"),
    ]);
  },

  async getByDateRange(
    clubId: string,
    startDate: Date,
    endDate: Date,
  ): Promise<Payment[]> {
    return getAll<Payment>("payments", [
      where("clubId", "==", clubId),
      where("paymentDate", ">=", Timestamp.fromDate(startDate)),
      where("paymentDate", "<=", Timestamp.fromDate(endDate)),
      orderBy("paymentDate", "desc"),
    ]);
  },

  async create(
    data: Omit<
      Payment,
      "id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
    >,
    userId: string,
  ): Promise<string> {
    // Crear el pago
    const paymentId = await create<Payment>("payments", data, userId);

    // Actualizar el cobro asociado
    const charge = await chargeService.getById(data.chargeId);
    if (charge) {
      const newAmountPaid = charge.amountPaid + data.amount;
      const newBalance = charge.amount - newAmountPaid;
      await chargeService.updateBalance(charge.id, newAmountPaid, newBalance);
    }

    return paymentId;
  },

  async reverse(id: string, reason: string, userId: string): Promise<void> {
    const payment = await this.getById(id);
    if (!payment) throw new Error("Pago no encontrado");

    // Marcar pago como reversado
    await update<Payment>("payments", id, {
      status: "reversed",
      reversedAt: Timestamp.now(),
      reversedBy: userId,
      reversalReason: reason,
    });

    // Revertir el cobro
    const charge = await chargeService.getById(payment.chargeId);
    if (charge) {
      const newAmountPaid = Math.max(0, charge.amountPaid - payment.amount);
      const newBalance = charge.amount - newAmountPaid;
      await chargeService.updateBalance(charge.id, newAmountPaid, newBalance);
    }
  },
};

// ==================== EXPENSES (GASTOS) ====================

export const expenseService = {
  async getById(id: string): Promise<Expense | null> {
    return getById<Expense>("expenses", id);
  },

  async getByClub(clubId: string): Promise<Expense[]> {
    return getAll<Expense>("expenses", [
      where("clubId", "==", clubId),
      orderBy("expenseDate", "desc"),
    ]);
  },

  async getByClubAndPeriod(
    clubId: string,
    periodId: string,
  ): Promise<Expense[]> {
    return getAll<Expense>("expenses", [
      where("clubId", "==", clubId),
      where("periodId", "==", periodId),
      orderBy("expenseDate", "desc"),
    ]);
  },

  async getByDateRange(
    clubId: string,
    startDate: Date,
    endDate: Date,
  ): Promise<Expense[]> {
    return getAll<Expense>("expenses", [
      where("clubId", "==", clubId),
      where("expenseDate", ">=", Timestamp.fromDate(startDate)),
      where("expenseDate", "<=", Timestamp.fromDate(endDate)),
      orderBy("expenseDate", "desc"),
    ]);
  },

  async create(
    data: Omit<
      Expense,
      "id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
    >,
    userId: string,
  ): Promise<string> {
    return create<Expense>("expenses", data, userId);
  },

  async update(id: string, data: Partial<Expense>): Promise<void> {
    return update<Expense>("expenses", id, data);
  },

  async cancel(id: string): Promise<void> {
    return update<Expense>("expenses", id, { status: "cancelled" });
  },
};

// ==================== HELPERS ====================

export interface FinancialSummary {
  totalCharges: number;
  totalPaid: number;
  totalPending: number;
  totalExpenses: number;
  netBalance: number;
  chargeCount: number;
  paymentCount: number;
  expenseCount: number;
}

export async function getFinancialSummary(
  clubId: string,
  periodId?: string,
): Promise<FinancialSummary> {
  const [charges, payments, expenses] = await Promise.all([
    periodId
      ? chargeService.getByClubAndPeriod(clubId, periodId)
      : chargeService.getByClub(clubId),
    periodId
      ? paymentService.getByClubAndPeriod(clubId, periodId)
      : paymentService.getByClub(clubId),
    periodId
      ? expenseService.getByClubAndPeriod(clubId, periodId)
      : expenseService.getByClub(clubId),
  ]);

  const validPayments = payments.filter((p) => p.status === "valid");
  const validExpenses = expenses.filter((e) => e.status === "valid");
  const activeCharges = charges.filter((c) => c.status !== "cancelled");

  const totalCharges = activeCharges.reduce((sum, c) => sum + c.amount, 0);
  const totalPaid = validPayments.reduce((sum, p) => sum + p.amount, 0);
  const totalPending = activeCharges.reduce((sum, c) => sum + c.balance, 0);
  const totalExpenses = validExpenses.reduce((sum, e) => sum + e.amount, 0);

  return {
    totalCharges,
    totalPaid,
    totalPending,
    totalExpenses,
    netBalance: totalPaid - totalExpenses,
    chargeCount: activeCharges.length,
    paymentCount: validPayments.length,
    expenseCount: validExpenses.length,
  };
}

// ==================== REALTIME SUBSCRIPTIONS ====================

export function subscribeToChargesByClubAndPeriod(
  clubId: string,
  periodId: string,
  callback: (charges: Charge[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Charge>(
    "charges",
    callback,
    [
      where("clubId", "==", clubId),
      where("periodId", "==", periodId),
      orderBy("createdAt", "desc"),
    ],
    onError,
  );
}

export function subscribeToChargesByClub(
  clubId: string,
  callback: (charges: Charge[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Charge>(
    "charges",
    callback,
    [where("clubId", "==", clubId), orderBy("createdAt", "desc")],
    onError,
  );
}

export function subscribeToPendingCharges(
  clubId: string,
  callback: (charges: Charge[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Charge>(
    "charges",
    callback,
    [
      where("clubId", "==", clubId),
      where("status", "in", ["pending", "partial", "overdue"]),
      orderBy("createdAt", "desc"),
    ],
    onError,
  );
}

export function subscribeToChargesByMember(
  memberId: string,
  callback: (charges: Charge[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Charge>(
    "charges",
    callback,
    [where("memberId", "==", memberId), orderBy("createdAt", "desc")],
    onError,
  );
}

export function subscribeToPaymentsByClubAndPeriod(
  clubId: string,
  periodId: string,
  callback: (payments: Payment[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Payment>(
    "payments",
    callback,
    [
      where("clubId", "==", clubId),
      where("periodId", "==", periodId),
      orderBy("paymentDate", "desc"),
    ],
    onError,
  );
}

export function subscribeToPaymentsByClub(
  clubId: string,
  callback: (payments: Payment[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Payment>(
    "payments",
    callback,
    [where("clubId", "==", clubId), orderBy("paymentDate", "desc")],
    onError,
  );
}

export function subscribeToPaymentsByMember(
  memberId: string,
  callback: (payments: Payment[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Payment>(
    "payments",
    callback,
    [where("memberId", "==", memberId), orderBy("paymentDate", "desc")],
    onError,
  );
}

export function subscribeToExpensesByClubAndPeriod(
  clubId: string,
  periodId: string,
  callback: (expenses: Expense[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Expense>(
    "expenses",
    callback,
    [
      where("clubId", "==", clubId),
      where("periodId", "==", periodId),
      orderBy("expenseDate", "desc"),
    ],
    onError,
  );
}

export function subscribeToExpensesByClub(
  clubId: string,
  callback: (expenses: Expense[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Expense>(
    "expenses",
    callback,
    [where("clubId", "==", clubId), orderBy("expenseDate", "desc")],
    onError,
  );
}

/** Suscripción realtime al resumen financiero (gestiona 3 suscripciones internas) */
export function subscribeToFinancialSummary(
  clubId: string,
  periodId: string | undefined,
  callback: (summary: FinancialSummary) => void,
  onError?: (error: Error) => void,
): () => void {
  let charges: Charge[] = [];
  let payments: Payment[] = [];
  let expenses: Expense[] = [];
  let ready = { charges: false, payments: false, expenses: false };

  const compute = () => {
    if (!ready.charges || !ready.payments || !ready.expenses) return;
    const validPayments = payments.filter((p) => p.status === "valid");
    const validExpenses = expenses.filter((e) => e.status === "valid");
    const activeCharges = charges.filter((c) => c.status !== "cancelled");
    const totalCharges = activeCharges.reduce((sum, c) => sum + c.amount, 0);
    const totalPaid = validPayments.reduce((sum, p) => sum + p.amount, 0);
    const totalPending = activeCharges.reduce((sum, c) => sum + c.balance, 0);
    const totalExpensesAmt = validExpenses.reduce(
      (sum, e) => sum + e.amount,
      0,
    );
    callback({
      totalCharges,
      totalPaid,
      totalPending,
      totalExpenses: totalExpensesAmt,
      netBalance: totalPaid - totalExpensesAmt,
      chargeCount: activeCharges.length,
      paymentCount: validPayments.length,
      expenseCount: validExpenses.length,
    });
  };

  const chargeConstraints = periodId
    ? [
        where("clubId", "==", clubId),
        where("periodId", "==", periodId),
        orderBy("createdAt", "desc"),
      ]
    : [where("clubId", "==", clubId), orderBy("createdAt", "desc")];
  const paymentConstraints = periodId
    ? [
        where("clubId", "==", clubId),
        where("periodId", "==", periodId),
        orderBy("paymentDate", "desc"),
      ]
    : [where("clubId", "==", clubId), orderBy("paymentDate", "desc")];
  const expenseConstraints = periodId
    ? [
        where("clubId", "==", clubId),
        where("periodId", "==", periodId),
        orderBy("expenseDate", "desc"),
      ]
    : [where("clubId", "==", clubId), orderBy("expenseDate", "desc")];

  const unsub1 = subscribeToCollection<Charge>(
    "charges",
    (data) => {
      charges = data;
      ready.charges = true;
      compute();
    },
    chargeConstraints,
    onError,
  );

  const unsub2 = subscribeToCollection<Payment>(
    "payments",
    (data) => {
      payments = data;
      ready.payments = true;
      compute();
    },
    paymentConstraints,
    onError,
  );

  const unsub3 = subscribeToCollection<Expense>(
    "expenses",
    (data) => {
      expenses = data;
      ready.expenses = true;
      compute();
    },
    expenseConstraints,
    onError,
  );

  return () => {
    unsub1();
    unsub2();
    unsub3();
  };
}

/** Suscripción en tiempo real a cobros de una persona */
export function subscribeToChargesByPerson(
  personId: string,
  callback: (charges: Charge[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Charge>(
    "charges",
    callback,
    [where("personId", "==", personId), orderBy("createdAt", "desc")],
    onError,
  );
}

/** Suscripción en tiempo real a pagos de un cobro */
export function subscribeToPaymentsByCharge(
  chargeId: string,
  callback: (payments: Payment[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Payment>(
    "payments",
    callback,
    [where("chargeId", "==", chargeId), orderBy("paymentDate", "desc")],
    onError,
  );
}

/** Suscripción en tiempo real a pagos de una persona */
export function subscribeToPaymentsByPerson(
  personId: string,
  callback: (payments: Payment[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Payment>(
    "payments",
    callback,
    [where("personId", "==", personId), orderBy("paymentDate", "desc")],
    onError,
  );
}
